"use client";

import clsx from "clsx";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Input from "@/components/library/Input";
import Modal from "@/components/library/Modal";

export type FormModalData = {
  labels: {
    label: string;
    isRequired: string;
    notValid: string;
  };
  type: "text" | "password" | "email" | "number" | "url" | "tel";
  value: string;
  regex?: RegExp;
  minimum?: number;
  maximum?: number;
  readOnly?: boolean;
  readOnlyHint?: string;
};

export type FormModalSection = {
  title: string;
  description?: string;
  fields: FormModalData[];
};

type FormModalDataInternal = FormModalData & { helperText: string };

type FormModalSectionInternal = {
  title: string;
  description?: string;
  fields: FormModalDataInternal[];
};

type FormModalProps = {
  labels: {
    title: string;
    description: string;
  };
  open: boolean;
  fields?: FormModalData[]; // For backward compatibility
  sections?: FormModalSection[]; // New sections prop
  cancel: () => void;
  save: (values: FormModalData[] | null) => Promise<void>;
};

export default function FormModal({
  labels,
  fields,
  sections,
  open,
  cancel,
  save,
}: FormModalProps) {
  const { t: ct } = useTranslation("common");
  const { t: vt } = useTranslation("validation");

  const [data, setData] = useState<FormModalSectionInternal[] | null>(null);

  useEffect(() => {
    if (open) {
      if (sections) {
        // Use sections if provided
        setData(
          sections.map((section) => ({
            title: section.title,
            description: section.description,
            fields: section.fields.map((field) => ({
              ...field,
              helperText: validateField(field),
            })),
          }))
        );
      } else if (fields) {
        // Fallback to single section for backward compatibility
        setData([
          {
            title: "",
            fields: fields.map((field) => ({
              ...field,
              helperText: validateField(field),
            })),
          },
        ]);
      }
    }
  }, [open, fields, sections]);

  const validateField = (field: FormModalData): string => {
    // Check if field is required and empty
    if (
      field.labels.isRequired &&
      (!field.value || field.value.toString().trim() === "")
    ) {
      return field.labels.isRequired;
    }

    // Skip validation if value is empty and not required
    if (!field.value || field.value.toString().trim() === "") {
      return "";
    }

    // Handle number fields differently
    if (field.type === "number") {
      const numValue = parseFloat(field.value);
      if (isNaN(numValue)) {
        return field.labels.notValid;
      }

      // Check if the field has minimum/maximum constraints
      if (field.minimum !== undefined && numValue < field.minimum) {
        return field.labels.notValid;
      }
      if (field.maximum !== undefined && numValue > field.maximum) {
        return field.labels.notValid;
      }

      // For number fields, if we have a regex, use it as additional validation
      if (field.regex && !field.regex.test(field.value)) {
        return field.labels.notValid;
      }
      return "";
    }

    // For non-number fields, use regex validation
    if (field.regex && !field.regex.test(field.value)) {
      return field.labels.notValid;
    }

    return "";
  };

  const handleOnChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    sectionIndex: number,
    fieldIndex: number,
    field: FormModalDataInternal
  ) => {
    const newData = data ? [...data] : [];
    const updatedField = {
      ...field,
      value: e.target.value,
    };

    newData[sectionIndex].fields[fieldIndex] = {
      ...updatedField,
      helperText: validateField(updatedField),
    };
    setData(newData);
  };

  const getAllFields = (): FormModalData[] => {
    if (!data) return [];
    return data.flatMap((section) => section.fields);
  };

  const hasErrors = (): boolean => {
    if (!data) return false;
    return data.some((section) =>
      section.fields.some((field) => field.helperText)
    );
  };

  return (
    <Modal
      open={open}
      onOpenChange={(isOpen) => !isOpen && cancel()}
      header={{
        title: labels.title,
        description: labels.description,
      }}
      buttons={[
        {
          type: "primary",
          onClick: () => save(getAllFields()),
          disabled: hasErrors(),
          children: ct("save"),
        },
        {
          type: "secondary",
          onClick: () => cancel(),
          children: ct("cancel"),
        },
      ]}
    >
      <div className={clsx("space-y-6")}>
        {data?.map((section, sectionIndex) => (
          <div key={sectionIndex} className="space-y-4">
            {section.title && (
              <div className="border-b border-gray-200 dark:border-gray-700 pb-2">
                <h3 className="text-lg font-semibold">{section.title}</h3>
                {section.description && (
                  <p className="text-sm text-600 mt-1">
                    {section.description}
                  </p>
                )}
              </div>
            )}
            <div className={clsx("grid", "grid-cols-1", "gap-4")}>
              {section.fields.map((field, fieldIndex) => (
                <div key={fieldIndex}>
                  <Input
                    type={field.type}
                    label={field.labels.label}
                    value={field.value}
                    helperText={field.helperText}
                    readOnly={field.readOnly}
                    disabled={field.readOnly}
                    tooltipText={field.readOnlyHint}
                    onChange={(e) =>
                      handleOnChange(e, sectionIndex, fieldIndex, field)
                    }
                  />
                  {/* Add helpful description for number inputs */}
                  {field.type === "number" && (
                    <p className="text-xs text-gray-500 mt-1">
                      {vt("validNumber")}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}
